import React, { useState, useRef, useEffect } from 'react';
import HrnbButton from '../controls/HrnbButton';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { normalizeMediaUrl } from '../../utils/mediaUrl';
import '../../css/components/AudioPlayer.css';

const API_BASE = 'https://api.hrnb.ru';

/**
 * Аудио плеер для воспроизведения треков
 * 
 * @component
 * @param {Object} props
 * @param {string} props.src - URL аудиофайла
 * @param {string} props.title - Название трека
 * @param {string} props.artist - Исполнитель (опционально)
 * @param {number} props.duration - Длительность в секундах (опционально)
 * @param {boolean} props.autoPlay - Автовоспроизведение
 * @param {Function} props.onEnded - Колбэк по окончании трека
 * @param {boolean} props.showDownload - Показать кнопку скачивания
 */
export default function AudioPlayer({
    src,
    title = 'Трек',
    artist,
    duration: propDuration,
    autoPlay = false,
    onEnded,
    showDownload = false
}) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(propDuration || 0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null);

    // Преобразуем относительный URL в абсолютный
    const getFullUrl = (url) => {
        if (!url) return '';
        // blob: и data: URL — локальные, используются как есть
        if (url.startsWith('blob:') || url.startsWith('data:')) {
            return url;
        }
        // Нормализуем (старый путь /api/files/{id}/download переписывается
        // на /api/files/download/audio/{id}, голый ID — в аудио-эндпойнт)
        const normalized = normalizeMediaUrl(url);
        if (/^https?:\/\//i.test(normalized)) {
            return normalized;
        }
        // Относительный путь - добавляем базовый URL (без /api, т.к. url уже содержит /api/...)
        return `${API_BASE}${normalized}`;
    };

    // Загружаем аудио через fetch с авторизацией и создаем Blob URL.
    // fetchWithAuth обновляет сессию при 401/истёкшем токене (как интерцепторы axios)
    const loadAudioWithAuth = async (url) => {
        try {
            const fullUrl = getFullUrl(url);
            console.log('[AudioPlayer] Fetching audio with auth:', fullUrl);

            const response = await fetchWithAuth(fullUrl);

            if (!response.ok) {
                if (response.status === 401) {
                    console.error('[AudioPlayer] Unauthorized - session refresh failed');
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();
            const newBlobUrl = URL.createObjectURL(blob);
            console.log('[AudioPlayer] Blob URL created:', newBlobUrl);
            return newBlobUrl;
        } catch (error) {
            console.error('[AudioPlayer] Failed to load audio:', error);
            throw error;
        }
    };

    // Логирование URL для отладки
    useEffect(() => {
        console.log('[AudioPlayer] src:', src);
        console.log('[AudioPlayer] blobUrl:', blobUrl ? 'loaded' : 'loading...');
    }, [src, blobUrl]);

    // Форматирование времени (секунды -> MM:SS)
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Загрузка метаданных
    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            const dur = audioRef.current.duration;
            setDuration(propDuration || dur || 0);
            setLoading(false);
        }
    };

    // Обновление текущего времени
    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    // Окончание трека
    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (onEnded) {
            onEnded();
        }
    };

    // Ошибка загрузки
    const handleError = (e) => {
        console.error('[AudioPlayer] Error loading audio:', e);
        console.error('[AudioPlayer] Source:', src);
        setError('Не удалось загрузить аудио');
        setLoading(false);
    };

    // Воспроизведение/пауза
    const togglePlay = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    // Перемотка
    const handleSeek = (e) => {
        const seekTime = parseFloat(e.target.value);
        if (audioRef.current) {
            audioRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    // Автовоспроизведение
    useEffect(() => {
        if (autoPlay && audioRef.current && !loading && !error) {
            audioRef.current.play()
                .then(() => setIsPlaying(true))
                .catch(err => console.warn('[AudioPlayer] Auto-play prevented:', err));
        }
    }, [autoPlay, loading, error]);

    // Сброс при смене трека и загрузка с авторизацией
    useEffect(() => {
        let newBlobUrl = null;

        const loadAudio = async () => {
            if (!src) {
                setError('No source URL');
                setLoading(false);
                return;
            }

            try {
                setIsPlaying(false);
                setCurrentTime(0);
                setLoading(true);
                setError(null);

                newBlobUrl = await loadAudioWithAuth(src);
                setBlobUrl(newBlobUrl);

                if (audioRef.current) {
                    audioRef.current.src = newBlobUrl;
                    audioRef.current.load();
                }
            } catch (err) {
                console.error('[AudioPlayer] Failed to load audio:', err);
                setError('Не удалось загрузить аудио');
                setLoading(false);
            }
        };

        loadAudio();

        // Очистка blob URL при размонтировании или смене трека
        return () => {
            if (newBlobUrl) {
                URL.revokeObjectURL(newBlobUrl);
            }
        };
    }, [src]);

    return (
        <div className="audio-player battle-audio-player">
            <audio
                ref={audioRef}
                src={blobUrl || undefined}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                onError={handleError}
            />

            <div className="audio-player__info">
                <div className="audio-player__title">{title}</div>
                {artist && <div className="audio-player__artist">{artist}</div>}
            </div>

            <div className="audio-player__controls">
                <HrnbButton
                    variant={isPlaying ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={togglePlay}
                    disabled={loading || error}
                    className="audio-player__play-btn"
                >
                    {isPlaying ? '⏸️' : '▶️'}
                </HrnbButton>

                <div className="audio-player__progress">
                    <span className="audio-player__time">
                        {formatTime(currentTime)}
                    </span>
                    
                    <input
                        type="range"
                        className="audio-player__seek"
                        min="0"
                        max={duration || 100}
                        value={currentTime}
                        onChange={handleSeek}
                        disabled={loading || error}
                    />
                    
                    <span className="audio-player__time">
                        {formatTime(duration)}
                    </span>
                </div>

                {showDownload && src && (
                    <a
                        href={blobUrl || getFullUrl(src)}
                        download
                        className="audio-player__download"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <HrnbButton variant="outline" size="sm">
                            ⬇️
                        </HrnbButton>
                    </a>
                )}
            </div>

            {loading && (
                <div className="audio-player__loading">
                    <small>Загрузка...</small>
                </div>
            )}

            {error && (
                <div className="audio-player__error">
                    <small>{error}</small>
                </div>
            )}
        </div>
    );
}
